import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    MarkerBreatheConstantType,
    MarkerStuckConstantType
} from "./type";
import {
    MOTION_EASINGS
} from "../../helpers/motion";

const SCROLL_OVERFLOW_PATTERN = /(auto|scroll|overlay)/;

const getScrollParent = (node: HTMLElement): HTMLElement | null => {
    let parent = node.parentElement;

    while(parent) {
        const overflow = globalThis.getComputedStyle(parent).overflowY;

        if(SCROLL_OVERFLOW_PATTERN.test(overflow)) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return null;
};

export const useMarkerBreathe = ({
    durations,
    isReduced,
    isPending
}: MarkerBreatheConstantType): Animated.Value | null => {
    const value = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if(!isPending || isReduced || Platform.OS === "web") {
            value.setValue(0);

            return;
        }

        const animation = Animated.loop(Animated.sequence([
            Animated.timing(value, {
                duration: durations.breathe / 2,
                easing: MOTION_EASINGS.inOut,
                useNativeDriver: false,
                isInteraction: false,
                toValue: 1
            }),
            Animated.timing(value, {
                duration: durations.breathe / 2,
                easing: MOTION_EASINGS.inOut,
                useNativeDriver: false,
                isInteraction: false,
                toValue: 0
            })
        ]));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        durations.breathe,
        isReduced,
        isPending,
        value
    ]);

    if(!isPending || isReduced || Platform.OS === "web") {
        return null;
    }

    return value;
};

export const useMarkerStuck = ({
    isStuck: isStuckProp,
    onStuckChange,
    isSticky,
    node,
    top
}: MarkerStuckConstantType): boolean => {
    const [
        isStuckState,
        setIsStuckState
    ] = useState<boolean>(false);

    const isControlled = isStuckProp !== undefined;

    useEffect(() => {
        if(Platform.OS !== "web" || !isSticky || isControlled || typeof IntersectionObserver === "undefined") {
            return;
        }

        const element = node.current;

        if(typeof HTMLElement === "undefined" || !(element instanceof HTMLElement)) {
            return;
        }

        const observer = new IntersectionObserver(([
            entry
        ]) => {
            if(!entry) {
                return;
            }

            const isNextStuck = entry.intersectionRatio < 1 && entry.boundingClientRect.top <= (entry.rootBounds?.top ?? 0) + top + 1;

            setIsStuckState(isNextStuck);

            onStuckChange?.(isNextStuck);
        }, {
            rootMargin: `-${top + 1}px 0px 0px 0px`,
            root: getScrollParent(element),
            threshold: [
                1
            ]
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [
        onStuckChange,
        isControlled,
        isSticky,
        node,
        top
    ]);

    return isControlled ? isStuckProp : isStuckState;
};
